import { useState, useCallback } from 'react';
import { useSpring, useTransition } from 'react-spring';

/**
 * Custom hook for managing animations
 * Provides common animation patterns
 */
export const useAnimation = (type = 'spring', config = {}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const defaultSpringConfig = {
    tension: 170,
    friction: 26,
    ...config,
  };

  const spring = useSpring({
    config: defaultSpringConfig,
    from: { opacity: 0, transform: 'scale(0.9)' },
    to: {
      opacity: isAnimating ? 1 : 0,
      transform: isAnimating ? 'scale(1)' : 'scale(0.9)',
    },
  });

  const trigger = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), config.duration || 1000);
  }, [config.duration]);

  return { spring, trigger, isAnimating };
};

/**
 * Hook for fade animation
 */
export const useFadeAnimation = (initialState = false) => {
  const [isVisible, setIsVisible] = useState(initialState);

  const fadeSpring = useSpring({
    opacity: isVisible ? 1 : 0,
    config: { duration: 300 },
  });

  return {
    style: fadeSpring,
    show: () => setIsVisible(true),
    hide: () => setIsVisible(false),
    toggle: () => setIsVisible(!isVisible),
    isVisible,
  };
};

/**
 * Hook for slide animation
 */
export const useSlideAnimation = (direction = 'left', initialState = false) => {
  const [isVisible, setIsVisible] = useState(initialState);

  const slideOffsets = {
    left: { transform: 'translateX(-100%)' },
    right: { transform: 'translateX(100%)' },
    up: { transform: 'translateY(-100%)' },
    down: { transform: 'translateY(100%)' },
  };

  const slideSpring = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translate(0%, 0%)' : slideOffsets[direction].transform,
    config: { tension: 280, friction: 60 },
  });

  return {
    style: slideSpring,
    show: () => setIsVisible(true),
    hide: () => setIsVisible(false),
    toggle: () => setIsVisible(!isVisible),
    isVisible,
  };
};

/**
 * Hook for scale animation
 */
export const useScaleAnimation = (initialState = false) => {
  const [isActive, setIsActive] = useState(initialState);

  const scaleSpring = useSpring({
    transform: isActive ? 'scale(1.05)' : 'scale(1)',
    config: { tension: 300, friction: 20 },
  });

  return {
    style: scaleSpring,
    activate: () => setIsActive(true),
    deactivate: () => setIsActive(false),
    toggle: () => setIsActive(!isActive),
    isActive,
  };
};

/**
 * Hook for list transitions
 */
export const useListTransition = (items) => {
  const transitions = useTransition(items, {
    keys: (item) => item.id || item.key,
    from: { opacity: 0, transform: 'translate3d(0,-40px,0)' },
    enter: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    leave: { opacity: 0, transform: 'translate3d(0,-40px,0)' },
    config: { tension: 280, friction: 60 },
  });

  return transitions;
};

export default useAnimation;
